import React, {useCallback, useEffect, useRef, useState} from 'react';
import {RUTAS} from 'constants/app';
import {useNavigate} from 'react-router-dom';
import '../styles/TokenPage.css';
import {useToken} from 'application/hooks/useToken';
import {useTheme} from '../providers/ThemeProvider';
import {flushPendingSubmissions} from 'application/services/PendingSubmissionsService';

const TokenPage = () => {
    const { theme } = useTheme();
    const [token, setToken] = useState('');
    const [isInputDisabled, setIsInputDisabled] = useState(false);

    // controla qué token estás validando
    const [submittedToken, setSubmittedToken] = useState<string | undefined>();
    // fuerza revalidación aunque el token sea el mismo (solo si hubo edición)
    const [refreshKey, setRefreshKey] = useState(0);
    // contador de edición del input
    const [editSeq, setEditSeq] = useState(0);
    const lastSubmittedEditSeq = useRef<number>(-1);

    const handledTokenRef = useRef<string | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const navigate = useNavigate();

    useEffect(() => {
        const flush = () => {
            flushPendingSubmissions().catch(err =>
                console.error('[pendientes] fallo al reintentar', err));
        };

        flush();
        window.addEventListener('online', flush);
        return () => window.removeEventListener('online', flush);
    }, []);

    const {
        loading,
        tokenStatus,
        isReady,
        campaignStartDate,
        campaignEndDate,
        guide,
        campaign,
        resolvedToken,
    } = useToken(submittedToken, refreshKey);

    type ErrorType =
        | 'warning'
        | 'invalid'
        | 'completed'
        | 'expired'
        | 'revoked'
        | 'outOfRange'
        | null;

    const [errorType, setErrorType] = useState<ErrorType>(null);

    const isDateInRange = useCallback((): boolean => {
        if (!campaignStartDate || !campaignEndDate) return false;
        const now = new Date();
        const start = new Date(campaignStartDate);
        const end = new Date(campaignEndDate);
        return now >= start && now <= end;
    }, [campaignStartDate, campaignEndDate]);

    const pulseButton = useCallback(() => {
        const btn = buttonRef.current;
        if (!btn) return;
        btn.classList.add('keyboard-pressed');
        window.setTimeout(() => btn.classList.remove('keyboard-pressed'), 130);
    }, []);

    const handleStart = useCallback(() => {
        pulseButton();

        // si ya está validando este mismo token, no dispares otra
        if (loading && submittedToken === token) return;

        // si no hay cambios desde el último submit del mismo token, no revalides
        if (token === submittedToken && editSeq === lastSubmittedEditSeq.current) return;

        if (token.trim().length === 0) {
            setErrorType('warning');
            return;
        }

        if (token.length !== 12) {
            setErrorType('invalid');
            return;
        }

        // listo para un nuevo intento del mismo token
        handledTokenRef.current = null;
        setErrorType(null);

        setSubmittedToken(token);
        setRefreshKey((k) => k + 1);
        lastSubmittedEditSeq.current = editSeq;
    }, [token, submittedToken, loading, editSeq, pulseButton]);

    useEffect(() => {
        if (!submittedToken) return;
        if (!isReady) return;
        if (tokenStatus === undefined) return;
        if (resolvedToken !== submittedToken) return;

        // idempotencia dentro del mismo intento
        if (handledTokenRef.current === submittedToken) return;

        const handleValidToken = (validToken: string) => {
            if (!isDateInRange()) {
                setErrorType('outOfRange');
                handledTokenRef.current = submittedToken;
                return;
            }

            const mapStr = localStorage.getItem("NOM035_tokenMap");
            const map = mapStr ? JSON.parse(mapStr) : {};
            const current = map[token] || {};

            if (current.finalizado) {
                setErrorType('completed');
                return;
            }

            const KEYS_TO_KEEP = ["NOM035_tokenMap"];
            Object.keys(localStorage).forEach((key) => {
                if (!KEYS_TO_KEEP.includes(key)) {
                    localStorage.removeItem(key);
                }
            });

            map[token] = {
                ...current,
                campaign: campaign,
                answers: current.answers || {},
                scores: current.scores || {},
            };

            localStorage.setItem('NOM035_tokenMap', JSON.stringify(map));

            localStorage.setItem('user-token', validToken);
            localStorage.setItem('guia', `GUIA_${guide}` || '');
            localStorage.setItem('campaign', campaign || '');

            handledTokenRef.current = submittedToken;
            navigate(`${RUTAS.BIENVENIDO}?token=${validToken}`);
        };

        switch (tokenStatus) {
            case 'COMPLETED':
                setErrorType('completed');
                handledTokenRef.current = submittedToken;
                break;
            case 'EXPIRED':
                setErrorType('expired');
                handledTokenRef.current = submittedToken;
                break;
            case 'REVOKED':
                setErrorType('revoked');
                handledTokenRef.current = submittedToken;
                break;
            case 'INVALID':
                setErrorType('invalid');
                handledTokenRef.current = submittedToken;
                break;
            case 'PENDING':
            case 'ACTIVE':
                handleValidToken(submittedToken);
                break;
            default:
                setErrorType('invalid');
                handledTokenRef.current = submittedToken;
                break;
        }
    }, [
        submittedToken,
        resolvedToken,
        isReady,
        tokenStatus,
        guide,
        campaign,
        navigate,
        isDateInRange,
        token
    ]);

    const canShowErrors = isReady && resolvedToken === token;
    // Visibilidad real de cada error (mismas condiciones que en el render)
    const isInvalidVisible = errorType === 'invalid' && (token.length !== 12 || canShowErrors);
    const isCompletedVisible = canShowErrors && errorType === 'completed';
    const isExpiredVisible = canShowErrors && errorType === 'expired';
    const isRevokedVisible = canShowErrors && errorType === 'revoked';
    const isOutOfRangeVisible = canShowErrors && errorType === 'outOfRange';
    const isWarningVisible = errorType === 'warning';

// Hay algún error mostrado en pantalla
    const isAnyErrorVisible = isWarningVisible || isInvalidVisible || isCompletedVisible ||
        isExpiredVisible || isRevokedVisible || isOutOfRangeVisible;

// Regla para desabilitar el botón
    const isButtonDisabled =
        (loading && submittedToken === token) || isAnyErrorVisible;


    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !e.repeat && !isButtonDisabled) {
                handleStart();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [handleStart, isButtonDisabled]);


    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');
        if (urlToken) {
            setToken(urlToken);
            setIsInputDisabled(urlToken.length === 12);
        }
    }, []);

    return (
        <div className="container-token-page">
            <img src={theme?.assets.logoHeader} alt="icono" className="logoAfirma"/>
            <img src={theme?.assets.elipseDecorativa} alt="decoración" className="elipse-decorativa"/>

            <div className="token-card">
                <img src={theme?.assets.logoMovil} alt="icono" className="logoMovil"/>
                <h1 className="title-token-page"
                    dangerouslySetInnerHTML={{__html: theme?.content.tokenPage.title || ''}}></h1>
                <p className="texto-token-page">
                    {theme?.content.tokenPage.description}
                </p>

                <input
                    type="text"
                    autoFocus={!isInputDisabled}
                    placeholder={theme?.content.tokenPage.inputPlaceholder}
                    className={`token-input 
            ${['invalid', 'completed', 'expired', 'revoked', 'outOfRange'].includes(errorType || '') ? 'token-error' : ''} 
            ${errorType === 'warning' ? 'token-warning' : ''}`}
                    value={token}
                    disabled={isInputDisabled}
                    onChange={(e) => {
                        setToken(e.target.value);
                        setSubmittedToken(undefined);
                        setErrorType(null);
                        handledTokenRef.current = null;
                        setEditSeq((s) => s + 1);
                    }}
                    maxLength={12}
                />

                {errorType === 'warning' && (
                    <div className="icono-warning">
                        <img src={theme?.assets.iconChatInfo} alt="icono advertencia"/>
                        <p className="token-warning-message">
                            {theme?.content.tokenPage.warningMessage}
                        </p>
                    </div>
                )}

                {(errorType === 'invalid') && (token.length !== 12 || canShowErrors) && (
                    <div className="icono-error">
                        <img src={theme?.assets.iconClose} alt="icono error" className="icono-error-close"/>
                        <p className="token-error-message">
                            {theme?.content.tokenPage.invalidTokenMessage}
                        </p>
                    </div>
                )}

                {canShowErrors && errorType === 'completed' && (
                    <div className="icono-error expirado">
                        <img src={theme?.assets.iconClose} alt="icono error" className="icono-error-close expirado"/>
                        <p className="token-error-message expirado">
                            {theme?.content.tokenPage.completedTokenMessage}
                        </p>
                    </div>
                )}

                {canShowErrors && errorType === 'expired' && (
                    <div className="icono-error">
                        <img src={theme?.assets.iconClose} alt="icono error" className="icono-error-close"/>
                        <p className="token-error-message">
                            {theme?.content.tokenPage.expiredTokenMessage}
                        </p>
                    </div>
                )}

                {canShowErrors && errorType === 'revoked' && (
                    <div className="icono-error">
                        <img src={theme?.assets.iconClose} alt="icono error" className="icono-error-close"/>
                        <p className="token-error-message">
                            {theme?.content.tokenPage.revokedTokenMessage}
                        </p>
                    </div>
                )}

                {canShowErrors && errorType === 'outOfRange' && (
                    <div className="icono-error">
                        <img src={theme?.assets.iconClose} alt="icono error" className="icono-error-close"/>
                        <p className="token-error-message">
                            {theme?.content.tokenPage.outOfRangeMessage}
                        </p>
                    </div>
                )}

                <button
                    ref={buttonRef}
                    className="btn welcome-button"
                    type="button"
                    onClick={handleStart}
                    disabled={isButtonDisabled}
                >
                    {theme?.content.tokenPage.buttonLabel}
                </button>

            </div>
        </div>
    );
};

export default TokenPage;
